import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import axios from "axios";
import { Dimmer } from "@/components/spinner";
import { formatMoneda } from "@/util/varios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// ---------------------------------------------------------
// Componente CustomTick (sin cambios)
// ---------------------------------------------------------
const CustomTick = (props) => {
  const { x, y, payload, datos } = props;
  const datoOriginal = datos ? datos[payload.index] : null;
  const monto = datoOriginal?.monto;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#333"
        transform="rotate(-45, 0, 0)"
      >
        <tspan x={0} dy="0" fill="#666" fontSize={12}>
          {payload.value}
        </tspan>
        {monto !== undefined && monto !== null && (
          <tspan x={0} dy="12" fontWeight="bold" fontSize={8}>
            {formatMoneda(monto.toString().replace(".", ","), ",", ".", 2)}
          </tspan>
        )}
      </text>
    </g>
  );
};

// ---------------------------------------------------------
// Configuración de rendimiento
// ---------------------------------------------------------
const CONFIG_PDF = {
  LOTE_TAMANIO: 5, // Cuántas gráficas por lote antes de pausar
  PAUSA_ENTRE_LOTES: 50, // ms de pausa entre lotes (respira el navegador)
  ESPERA_GRAFICA: 250, // ms de espera después de renderizar cada gráfica
  ESPERA_WARMUP: 600, // ms para el warm-up inicial
  CANVAS_SCALE: 1.5, // Calidad del canvas (1 = baja, 2 = alta, 1.5 = balance)
  LIMITE_SIN_CONFIRMAR: 30, // Si hay más de esto, pregunta al usuario
};

export default function ModalDetalladoContribuyentes(props) {
  const op = require("@/modulos/datos");
  const token = localStorage.getItem("jwtToken");
  const [loading, setLoading] = useState(false);
  const [moneda, setMoneda] = useState("Bs");
  const [body, setBody] = useState([]);
  const [itemParaPDF, setItemParaPDF] = useState(null);
  const chartIndividualRef = useRef(null);

  // ============================================
  // NUEVO: Estado para el progreso del PDF
  // ============================================
  const [progresoPDF, setProgresoPDF] = useState({
    activo: false,
    actual: 0,
    total: 0,
    porcentaje: 0,
    mensaje: "",
  });

  const cancelarPDF = useRef(false); // Para poder cancelar

  const buscarRegistros = async () => {
    try {
      setLoading(true);
      const endpoint = `${op.conexion}/api/industria/detalleComerciosDeclarados?moneda=${moneda}&anio=${props.anio}&limite=${props.limite}`;
      const response = await axios.get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
          "Content-Type": "application/json",
        },
      });
      setBody(response.status === 200 && response.data ? response.data : []);
    } catch (error) {
      setBody([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!props.show) return;
    buscarRegistros();
  }, [moneda, props.anio, props.show]);

  const salir = () => {
    cancelarPDF.current = true; // Cancelar generación si está en curso
    setMoneda("Bs");
    setLoading(false);
    setBody([]);
    setItemParaPDF(null);
    setProgresoPDF({
      activo: false,
      actual: 0,
      total: 0,
      porcentaje: 0,
      mensaje: "",
    });
    props.onHide();
  };

  const textoMoneda =
    moneda === "Bs"
      ? "Bolivares (Bs)"
      : moneda === "dolar"
        ? "Dolares ($)"
        : "Unidad (€)";

  // ============================================
  // Función auxiliar: Pausa que no bloquea el hilo
  // ============================================
  const pausar = (ms) =>
    new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      // Guardar timer por si se necesita cancelar
      return () => clearTimeout(timer);
    });

  // ============================================
  // Función auxiliar: Ceder el control al navegador
  // ============================================
  const cederControl = () =>
    new Promise((resolve) => requestAnimationFrame(() => resolve()));

  // ============================================
  // PDF Optimizado para Grandes Volúmenes
  // ============================================
  // ---------------------------------------------------------
  // FUNCIÓN CLAVE: Detecta cuándo la gráfica está realmente lista
  // ---------------------------------------------------------
  const esperarGraficaLista = (ref, timeoutMax = 500) => {
    return new Promise((resolve) => {
      const inicio = Date.now();

      const verificar = () => {
        if (!ref.current) {
          resolve();
          return;
        }

        // Buscar los rectángulos de las barras en el SVG
        const barras = ref.current.querySelectorAll(
          ".recharts-bar-rectangles rect",
        );

        // Verificar que:
        // 1. Existen barras
        // 2. Al menos una tiene altura > 0
        const lista =
          barras.length > 0 &&
          Array.from(barras).some((barra) => {
            const height = parseFloat(barra.getAttribute("height") || "0");
            return height > 0;
          });

        if (lista) {
          // Pequeña pausa de seguridad (15ms) y resolvemos
          setTimeout(resolve, 15);
        } else if (Date.now() - inicio > timeoutMax) {
          // Timeout: continuar de todos modos para no quedarse atascado
          console.warn("Timeout esperando gráfica, continuando...");
          resolve();
        } else {
          // Seguir verificando en el siguiente frame
          requestAnimationFrame(verificar);
        }
      };

      // Empezar a verificar después de un micro-delay
      setTimeout(verificar, 10);
    });
  };

  // ---------------------------------------------------------
  // PDF Ultra-Rápido
  // ---------------------------------------------------------
  const generarPDF = async () => {
    if (body.length === 0) return;

    // Confirmación para muchos registros
    if (body.length > CONFIG_PDF.LIMITE_SIN_CONFIRMAR) {
      const confirmacion = window.confirm(
        `Se generarán ${body.length} gráficas.\n\n¿Desea continuar?`,
      );
      if (!confirmacion) return;
    }

    try {
      setLoading(true);
      cancelarPDF.current = false;

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let posicionY = 20;

      setProgresoPDF({
        activo: true,
        actual: 0,
        total: body.length,
        porcentaje: 0,
        mensaje: "Inicializando...",
      });

      // ============================================
      // WARM-UP (única vez, con detección inteligente)
      // ============================================
      const datosWarmup = {
        nombre: "warmup",
        monto_total: 0,
        meses: body[0].meses.map((m) => ({ ...m })),
      };

      setItemParaPDF(datosWarmup);
      await esperarGraficaLista(chartIndividualRef, 1000);
      setItemParaPDF(null);
      await new Promise((r) => setTimeout(r, 50));

      if (cancelarPDF.current) return;

      // ============================================
      // Cabecera
      // ============================================
      pdf.setFontSize(16);
      pdf.setTextColor(78, 115, 223);
      pdf.text("Detallado de Contribuyentes", pageWidth / 2, posicionY, {
        align: "center",
      });
      posicionY += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(
        `Año: ${props.anio} | Moneda: ${textoMoneda} | Total: ${body.length}`,
        pageWidth / 2,
        posicionY,
        { align: "center" },
      );
      posicionY += 15;

      // ============================================
      // CICLO PRINCIPAL - Detección inteligente
      // ============================================
      for (let i = 0; i < body.length; i++) {
        if (cancelarPDF.current) return;

        const item = body[i];

        setProgresoPDF({
          activo: true,
          actual: i + 1,
          total: body.length,
          porcentaje: Math.round(((i + 1) / body.length) * 100),
          mensaje: `Gráfica ${i + 1} de ${body.length}`,
        });

        // Renderizar
        setItemParaPDF(item);

        // ESPERAR INTELIGENTEMENTE (no tiempo fijo)
        await esperarGraficaLista(chartIndividualRef, 400);

        // Capturar
        const canvas = await html2canvas(chartIndividualRef.current, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.8); // JPEG más rápido que PNG
        const imgWidth = 170;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Salto de página
        if (posicionY + imgHeight + 25 > pageHeight) {
          pdf.addPage();
          posicionY = 20;
        }

        // Textos
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${i + 1}. ${item.nombre}`, 15, posicionY);

        pdf.setFontSize(9);
        pdf.setTextColor(78, 115, 223);
        pdf.text(
          formatMoneda(
            item.monto_total.toString().replace(".", ","),
            ",",
            ".",
            2,
          ),
          pageWidth - 15,
          posicionY,
          { align: "right" },
        );
        posicionY += 5;

        // Imagen
        pdf.addImage(imgData, "JPEG", 15, posicionY, imgWidth, imgHeight);
        posicionY += imgHeight + 12;

        // Limpiar
        setItemParaPDF(null);

        // Ceder control cada 8 gráficas (no cada 5)
        if ((i + 1) % 8 === 0 && i < body.length - 1) {
          await new Promise((r) => requestAnimationFrame(r));
        }
      }

      if (cancelarPDF.current) return;

      // Descargar
      setProgresoPDF((p) => ({
        ...p,
        porcentaje: 100,
        mensaje: "Abriendo PDF...",
      }));

      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "Planilla", "width=850,height=700");
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setItemParaPDF(null);
      setLoading(false);
      setTimeout(() => {
        setProgresoPDF({
          activo: false,
          actual: 0,
          total: 0,
          porcentaje: 0,
          mensaje: "",
        });
      }, 1500);
    }
  };
  const generarReporte = () => {
    window.open(
      `${op.reportes}/actividadeconomica?reporte=grafica&ANIO=${props.anio}&MONEDA=${moneda}&LIMIT=${props.limite}`,
      "Planilla",
      "fullscreen=no,scrollbars=yes,toolbar=no,resizable=no,width=850,height=700",
    );
  };
  // ============================================
  // COMPONENTE DE BARRA DE PROGRESO
  // ============================================
  const BarraProgreso = () => {
    if (!progresoPDF.activo) return null;

    return (
      <div
        className="position-fixed top-0 start-0 w-100 z-3"
        style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
      >
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-white fw-bold">
                  <i className="fas fa-file-pdf me-2"></i>
                  Generando PDF...
                </span>
                <span className="text-white">
                  {progresoPDF.actual}/{progresoPDF.total} (
                  {progresoPDF.porcentaje}%)
                </span>
              </div>

              {/* Barra de progreso */}
              <div
                className="progress"
                style={{
                  height: "25px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                }}
              >
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{
                    width: `${progresoPDF.porcentaje}%`,
                    backgroundColor: "#4e73df",
                  }}
                >
                  <span className="text-white fw-bold small">
                    {progresoPDF.porcentaje}%
                  </span>
                </div>
              </div>

              {/* Mensaje de estado */}
              <div className="text-white-50 mt-2 small">
                {progresoPDF.mensaje}
              </div>

              {/* Botón cancelar */}
              <button
                className="btn btn-sm btn-outline-light mt-2"
                onClick={() => {
                  cancelarPDF.current = true;
                }}
              >
                <i className="fas fa-times me-1"></i> Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* BARRA DE PROGRESO (se superpone sobre todo) */}
      <BarraProgreso />

      {/* ========================================== */}
      {/* MODAL ORIGINAL                             */}
      {/* ========================================== */}
      <Modal
        {...props}
        size="xl"
        centered
        backdrop="static"
        className="pt-0 mt-0"
      >
        <Modal.Header
          className="azulGradiant px-4 py-2 text-uppercase"
          style={{ marginTop: -20 }}
        >
          <div className="d-flex align-items-center gap-3">
            <h3 className="fs-4 fw-bold text-light m-0">
              Detallado de Contribuyentes
            </h3>
            {body.length > 0 && (
              <span className="badge bg-light text-dark">
                {body.length} registros
              </span>
            )}
          </div>
          <button className="btn btn-sm bg-light" onClick={salir}>
            <i className="fa fa-times"></i>
          </button>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "85vh", overflowY: "auto" }}>
          {/* Selectores de moneda */}
          <div className="row g-3 mb-3">
            <div className="col-md-12">
              <div className="col-md-6 row justify-content-end text-end ml-auto">
                <div className="col-md-3">
                  <div className="form-check d-inline-flex align-items-center">
                    <label className="form-check-label d-flex align-items-center">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="tipoReporte"
                        value="Bs"
                        checked={moneda === "Bs"}
                        onChange={(e) => setMoneda(e.target.value)}
                      />
                      Bolivares (Bs)
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check d-inline-flex align-items-center">
                    <label className="form-check-label d-flex align-items-center">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="tipoReporte"
                        value="unidad"
                        checked={moneda === "unidad"}
                        onChange={(e) => setMoneda(e.target.value)}
                      />
                      Unidad (€)
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check d-inline-flex align-items-center">
                    <label className="form-check-label d-flex align-items-center">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="tipoReporte"
                        value="dolar"
                        checked={moneda === "dolar"}
                        onChange={(e) => setMoneda(e.target.value)}
                      />
                      Dolares ($)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficas */}
          {body.length > 0
            ? body.map((item, index) => (
                <div
                  className="col-12 col-md-12 mb-3"
                  key={item.idcomercio || index}
                >
                  <div className="border rounded shadow-sm p-3 bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6
                        className="mb-0 text-truncate"
                        style={{ maxWidth: "70%", fontSize: "0.9rem" }}
                        title={item.nombre}
                      >
                        {index + 1}. {item.nombre} - {item.rif}
                      </h6>
                      <span className="badge bg-primary">
                        Total:{" "}
                        {formatMoneda(
                          item.monto_total.toString().replace(".", ","),
                          ",",
                          ".",
                          2,
                        )}
                      </span>
                    </div>
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart
                        data={item.meses}
                        margin={{ top: 5, right: 10, left: -10, bottom: 40 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          tick={<CustomTick datos={item.meses} />}
                        />
                        <Tooltip
                          formatter={(value) =>
                            formatMoneda(
                              value.toString().replace(".", ","),
                              ",",
                              ".",
                              2,
                            )
                          }
                        />
                        <Bar dataKey="monto" radius={[4, 4, 0, 0]}>
                          {item.meses.map((mes, idx) => (
                            <Cell
                              key={`cell-${idx}`}
                              fill={mes.pagado ? "#4e73df" : "#FF0000"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ))
            : !loading && (
                <div className="col-12 text-center text-muted mt-5">
                  No se encontraron registros.
                </div>
              )}
        </Modal.Body>

        <Modal.Footer>
          <button
            className="btn-sm rounded-pill btn btn-primary col-md-2"
            onClick={() => generarReporte()}
            disabled={loading || body.length === 0 || progresoPDF.activo}
          >
            <i className="fas fa-file-pdf"></i>{" "}
            {progresoPDF.activo ? "Generando Reporte..." : "Generando Reporte"}
          </button>
          <button
            onClick={salir}
            className="btn-sm rounded-pill btn btn-danger col-md-2"
          >
            <i className="fa fa-sign-out"></i> Salir
          </button>
        </Modal.Footer>
        <Dimmer active={loading} inverted />
      </Modal>

      {/* {itemParaPDF && (
        <div
          ref={chartIndividualRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: "0",
            width: "600px", // Reducido de 700px para menor carga
            background: "white",
            padding: "10px",
          }}
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={itemParaPDF.meses}
              margin={{ top: 5, right: 15, left: 10, bottom: 40 }}
              isAnimationActive={false}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={<CustomTick datos={itemParaPDF.meses} />}
              />
              <Tooltip
                formatter={(value) =>
                  formatMoneda(value.toString().replace(".", ","), ",", ".", 2)
                }
              />
              <Bar dataKey="monto" radius={[4, 4, 0, 0]}>
                {itemParaPDF.meses.map((mes, index) => (
                  <Cell
                    key={`cell-pdf-${index}`}
                    fill={mes.pagado ? "#4e73df" : "#FF0000"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )} */}
    </>
  );
}
